/**
 * @license Copyright Shutterfly, Inc. 2017 - all rights reserved
 * Originator: Shawn Rapp
 * Description: Provides a class and instance for lazyLoading scripts and styles.
 */

/**
 * Simple XHR or JSONP handler
 * @class HttpHandler
 */
 export class HttpHandler {
     /**
      * This method establishes a connection to the server that
      * allows you to specify what HTTP method should be passed.
      * It is the principal method for the other methods to use.
      *
      * @method connect
      * @param method {string} HTTP method type to use.
      * @param url {string} The url to the HTTP server.
      * @param params {object} Parameters to pass through the HTTP session
      * @param cb {Function} callback to be called with results or error
      */
    public connect(method: string, url: string, params: object, cb: (results: object, error: object) => void) {
        const xhr = new XMLHttpRequest();
        xhr.open(method, url, true);
        xhr.onreadystatechange = () => {
            if (xhr.readyState === 4) {
                let data: object;
                try {
                    data = JSON.parse(xhr.responseText);
                } catch (err) {
                    console.error(err);
                    return cb(null, err);
                }

                if (cb) {
                    cb(data, null);
                }
            }
        };

        let body;
        if (params) {
            const bodies = [];
            for (const name in params) {
                if (params.hasOwnProperty[name]) {
                    bodies.push(name + "=" + encodeURIComponent(params[name]));
                }
            }

            body = bodies.join("&");
            if (body.length) {
                xhr.setRequestHeader("Content-type", "application/x-www-form-urlencoded");
            }
        }

        xhr.send(body);
    }

    /**
     * This function places a JSONP session and returns data.
     * Avoid if at all possible!
     * Modern browser still corral JSONP through CORS.
     *
     * @method jsonp
     * @param url {string} URL to call
     * @param callback {Function} callback that returns data.
     */
    public jsonp(url: string, callback: (error: any, data: any) => void) {
        const callbackName = "jsonp_callback_" + Math.round(100000 * Math.random());
        window[callbackName] = (data) => {
                delete window[callbackName];
                document.body.removeChild(script);
                callback(null, data);
        };

        const script = document.createElement("script");

        script.src = url;
        script.onerror = (err) => {
            callback(err, null);
        };

        document.body.appendChild(script);
    }

    /**
     * Connect via GET method.
     *
     * @method get
     * @param url {string} URL to endpoint.
     * @param params {object} Object containing parameters.
     * @param cb {Function} Callback that returns result data or error.
     */
    public get(url, params, cb) {
        this.connect("GET", url, params, cb);
    }

    /**
     * Connect via POST method.
     *
     * @method post
     * @param url {string} URL to endpoint.
     * @param params {object} Object containing parameters.
     * @param cb {Function} Callback that returns result data or error.
     */
    public post(url, params, cb) {
        this.connect("POST", url, params, cb);
    }

    /**
     * Connect via DELETE method.
     *
     * @method del
     * @param url {string} URL to endpoint.
     * @param params {object} Object containing parameters.
     * @param cb {Function} Callback that returns result data or error.
     */
    public del(url, params, cb) {
        this.connect("DELETE", url, params, cb);
    }

    /**
     * Connect via PUT method.
     *
     * @method put
     * @param url {string} URL to endpoint.
     * @param params {object} Object containing parameters.
     * @param cb {Function} Callback that returns result data or error.
     */
    public put(url, params, cb) {
        this.connect("PUT", url, params, cb);
    }

    /**
     * Connect via HEAD method.
     *
     * @method head
     * @param url {string} URL to endpoint.
     * @param params {object} Object containing parameters.
     * @param cb {Function} Callback that returns result data or error.
     */
    public head(url, params, cb) {
        this.connect("HEAD", url, params, cb);
    }

    /**
     * Connect via OPTIONS method.
     *
     * @method options
     * @param url {string} URL to endpoint.
     * @param params {object} Object containing parameters.
     * @param cb {Function} Callback that returns result data or error.
     */
    public options(url, params, cb) {
        this.connect("OPTIONS", url, params, cb);
    }
}
